<template>
  <div class="japan-power-market">
    <!-- Hero Section -->
    <section class="ct-hero-section japan-power-hero">
      <div class="container ct-hero-content">
        <div class="row align-items-center">
          <div class="col-lg-6">
            <h1 class="display-4 fw-bold mb-4" v-html="t('markets.japanPower.hero.title')"></h1>
            <p class="lead mb-4">{{ t('markets.japanPower.hero.subtitle') }}</p>
            <router-link to="/contact" class="ct-btn-primary me-3">{{ t('markets.japanPower.hero.requestDemo') }}</router-link>
            <a href="#features" class="ct-btn-outline">{{ t('markets.japanPower.hero.exploreFeatures') }}</a>
          </div>
          <div class="col-lg-6">
            <div class="market-stats-card">
              <h3>{{ t('markets.japanPower.marketOverview.title') }}</h3>
              <div class="market-stat-row">
                <div class="market-stat">
                  <span class="stat-label">{{ t('markets.japanPower.marketOverview.systemPrice') }}</span>
                  <span class="stat-value">¥11.89/kWh</span>
                  <span class="stat-change positive">+1.2%</span>
                </div>
                <div class="market-stat">
                  <span class="stat-label">{{ t('markets.japanPower.marketOverview.volume') }}</span>
                  <span class="stat-value">147.5 GWh</span>
                </div>
              </div>
              <div class="market-stat-row">
                <div class="market-stat">
                  <span class="stat-label">{{ t('markets.japanPower.marketOverview.openInterest') }}</span>
                  <span class="stat-value">1,245 Contracts</span>
                </div>
                <div class="market-stat">
                  <span class="stat-label">{{ t('markets.japanPower.marketOverview.lastUpdated') }}</span>
                  <span class="stat-value">{{ currentTime }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    <!-- Japan Map Section -->
    <section id="japan-map" class="ct-section japan-map-section">
      <div class="container">
        <div class="row">
          <div class="col-lg-5">
            <div class="map-info-content">
              <h2 class="ct-title">{{ t('markets.japanPower.japanMap.title') }}</h2>
              <p class="mb-4">{{ t('markets.japanPower.japanMap.description') }}</p>

              <div class="market-category">
                <h3 class="market-category-title">
                  <i class="fas fa-chart-line me-2"></i> {{ t('markets.japanPower.japanMap.financialMarkets') }}
                </h3>

                <div class="region-cards">
                  <div class="region-card financial" @mouseover="highlightRegion('financial-tokyo')" @mouseout="resetHighlight">
                    <div class="region-card-badge">{{ t('markets.japanPower.japanMap.futuresBadge') }}</div>
                    <h4>{{ t('markets.japanPower.japanMap.regions.tokyo.name') }}</h4>
                    <p>{{ t('markets.japanPower.japanMap.regions.tokyo.description') }}</p>
                  </div>

                  <div class="region-card financial" @mouseover="highlightRegion('financial-kansai')" @mouseout="resetHighlight">
                    <div class="region-card-badge">{{ t('markets.japanPower.japanMap.futuresBadge') }}</div>
                    <h4>{{ t('markets.japanPower.japanMap.regions.kansai.name') }}</h4>
                    <p>{{ t('markets.japanPower.japanMap.regions.kansai.description') }}</p>
                  </div>
                </div>
              </div>

              <div class="market-category mt-4">
                <h3 class="market-category-title">
                  <i class="fas fa-bolt me-2"></i> {{ t('markets.japanPower.japanMap.physicalMarkets') }}
                </h3>

                <div class="frequency-tabs">
                  <div class="tab-header">
                    <div class="tab-item active" data-tab="50hz">
                      <span class="frequency-chip">50Hz</span>
                      <span>{{ t('markets.japanPower.japanMap.easternJapan') }}</span>
                    </div>
                    <div class="tab-item" data-tab="60hz">
                      <span class="frequency-chip">60Hz</span>
                      <span>{{ t('markets.japanPower.japanMap.westernJapan') }}</span>
                    </div>
                  </div>

                  <div class="tab-content">
                    <div class="tab-pane active" id="50hz-tab">
                      <div class="region-grid">
                        <div class="region-card hz50" @mouseover="highlightRegion('hokkaido')" @mouseout="resetHighlight">
                          <h4>{{ t('markets.japanPower.japanMap.regions.hokkaido') }}</h4>
                        </div>
                        <div class="region-card hz50" @mouseover="highlightRegion('tohoku')" @mouseout="resetHighlight">
                          <h4>{{ t('markets.japanPower.japanMap.regions.tohoku') }}</h4>
                        </div>
                        <div class="region-card hz50" @mouseover="highlightRegion('tepco')" @mouseout="resetHighlight">
                          <h4>{{ t('markets.japanPower.japanMap.regions.tepco') }}</h4>
                        </div>
                      </div>
                    </div>

                    <div class="tab-pane" id="60hz-tab">
                      <div class="region-grid">
                        <div class="region-card hz60" @mouseover="highlightRegion('kansai')" @mouseout="resetHighlight">
                          <h4>{{ t('markets.japanPower.japanMap.regions.kansai.name2') }}</h4>
                        </div>
                        <div class="region-card hz60" @mouseover="highlightRegion('hokuriku')" @mouseout="resetHighlight">
                          <h4>{{ t('markets.japanPower.japanMap.regions.hokuriku') }}</h4>
                        </div>
                        <div class="region-card hz60" @mouseover="highlightRegion('chubu')" @mouseout="resetHighlight">
                          <h4>{{ t('markets.japanPower.japanMap.regions.chubu') }}</h4>
                        </div>
                        <div class="region-card hz60" @mouseover="highlightRegion('chugoku')" @mouseout="resetHighlight">
                          <h4>{{ t('markets.japanPower.japanMap.regions.chugoku') }}</h4>
                        </div>
                        <div class="region-card hz60" @mouseover="highlightRegion('shikoku')" @mouseout="resetHighlight">
                          <h4>{{ t('markets.japanPower.japanMap.regions.shikoku') }}</h4>
                        </div>
                        <div class="region-card hz60" @mouseover="highlightRegion('kyushu')" @mouseout="resetHighlight">
                          <h4>{{ t('markets.japanPower.japanMap.regions.kyushu') }}</h4>
                        </div>
                        <div class="region-card hz60" @mouseover="highlightRegion('okinawa')" @mouseout="resetHighlight">
                          <h4>{{ t('markets.japanPower.japanMap.regions.okinawa') }}</h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-lg-7">
            <div class="japan-map-container">
              <JapanMap ref="japanMap" />
              <div class="map-overlay">
                <div class="region-data-box" v-if="activeRegion && activeRegionData">
                  <h4>{{ getRegionName(activeRegion) }}</h4>
                  <div class="region-data-row">
                    <span>{{ t('markets.japanPower.regionData.labels.basePrice') }}:</span>
                    <span>{{ getRegionData(activeRegion, 'basePrice') }}</span>
                  </div>
                  <div class="region-data-row">
                    <span>{{ t('markets.japanPower.regionData.labels.peakPrice') }}:</span>
                    <span>{{ getRegionData(activeRegion, 'peakPrice') }}</span>
                  </div>
                  <div class="region-data-row">
                    <span>{{ t('markets.japanPower.regionData.labels.generationMix') }}:</span>
                    <span>{{ getRegionData(activeRegion, 'generationMix') }}</span>
                  </div>
                  <div class="region-data-row">
                    <span>{{ t('markets.japanPower.regionData.labels.frequency') }}:</span>
                    <span>{{ getRegionData(activeRegion, 'frequency') }}</span>
                  </div>
                  <div class="region-data-row" v-if="isFinancialRegion(activeRegion)">
                    <span>{{ t('markets.japanPower.regionData.labels.tradingVolume') }}:</span>
                    <span>{{ getRegionData(activeRegion, 'tradingVolume') }}</span>
                  </div>
                  <div class="region-data-row" v-if="isFinancialRegion(activeRegion)">
                    <span>{{ t('markets.japanPower.regionData.labels.openInterest') }}:</span>
                    <span>{{ getRegionData(activeRegion, 'openInterest') }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section id="features" class="ct-section">
      <div class="container">
        <div class="text-center mb-5">
          <h2 class="ct-title">{{ t('markets.japanPower.features.title') }}</h2>
          <p class="ct-subtitle">{{ t('markets.japanPower.features.subtitle') }}</p>
        </div>

        <!-- Exchange Data Section -->
        <div class="feature-category mb-5">
          <h3 class="feature-category-title">
            <i class="fas fa-exchange-alt me-2"></i> {{ t('markets.japanPower.features.exchangeData.title') }}
          </h3>

          <div class="row g-4">
            <!-- Live EEX CLOB Market -->
            <div class="col-lg-4">
              <div class="ct-card feature-card">
                <div class="feature-header">
                  <div class="feature-icon-wrap">
                    <i class="fas fa-chart-line feature-icon"></i>
                  </div>
                  <h4>{{ t('markets.japanPower.features.exchangeData.eexClob.title') }}</h4>
                  <span class="data-license-badge" v-html="t('markets.japanPower.features.exchangeData.eexClob.badge')"></span>
                </div>
                <p>{{ t('markets.japanPower.features.exchangeData.eexClob.description') }}</p>
                <div class="feature-preview">
                  <div class="clob-preview">
                    <div class="clob-header">
                      <span class="contract-header">Contract</span>
                      <span class="qty-header">Qty</span>
                      <span class="bid-header">Bid</span>
                      <span class="ask-header">Ask</span>
                      <span class="qty-header">Qty</span>
                    </div>
                    <div class="clob-row">
                      <span class="contract-name">Base Jul-24</span>
                      <span class="bid-qty">5</span>
                      <span class="bid-price">13.50</span>
                      <span class="ask-price">13.75</span>
                      <span class="ask-qty">10</span>
                    </div>
                    <div class="clob-row">
                      <span class="contract-name">Base Aug-24</span>
                      <span class="bid-qty">2</span>
                      <span class="bid-price">14.25</span>
                      <span class="ask-price">14.50</span>
                      <span class="ask-qty">5</span>
                    </div>
                    <div class="clob-row">
                      <span class="contract-name">Base Q3-24</span>
                      <span class="bid-qty">10</span>
                      <span class="bid-price">13.20</span>
                      <span class="ask-price">13.35</span>
                      <span class="ask-qty">15</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- EEX & TOCOM Settlement Prices -->
            <div class="col-lg-4">
              <div class="ct-card feature-card">
                <div class="feature-header">
                  <div class="feature-icon-wrap">
                    <i class="fas fa-calendar-check feature-icon"></i>
                  </div>
                  <h4>{{ t('markets.japanPower.features.exchangeData.settlement.title') }}</h4>
                </div>
                <p>{{ t('markets.japanPower.features.exchangeData.settlement.description') }}</p>
                <div class="feature-preview">
                  <div class="settlement-preview">
                    <div class="exchange-tabs">
                      <span class="active">EEX</span>
                      <span>TOCOM</span>
                    </div>
                    <div class="settlement-table">
                      <div class="table-header">
                        <span>Contract</span>
                        <span>Settlement</span>
                        <span>Change</span>
                      </div>
                      <div class="table-row">
                        <span>Base Q3-24</span>
                        <span>¥13.75</span>
                        <span class="positive">+0.35</span>
                      </div>
                      <div class="table-row">
                        <span>Base Cal-25</span>
                        <span>¥12.40</span>
                        <span class="negative">-0.20</span>
                      </div>
                      <div class="table-row">
                        <span>Peak Aug-24</span>
                        <span>¥16.65</span>
                        <span class="positive">+0.75</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Live EEX Block Trades -->
            <div class="col-lg-4">
              <div class="ct-card feature-card">
                <div class="feature-header">
                  <div class="feature-icon-wrap">
                    <i class="fas fa-cubes feature-icon"></i>
                  </div>
                  <h4>{{ t('markets.japanPower.features.exchangeData.blockTrades.title') }}</h4>
                </div>
                <p>{{ t('markets.japanPower.features.exchangeData.blockTrades.description') }}</p>
                <div class="recent-trades">
                  <div class="trades-scrollable">
                    <div class="trade-item" v-for="(trade, index) in recentTrades" :key="index">
                      <div class="trade-time">{{ trade.time }}</div>
                      <div class="trade-details">
                        <span class="trade-contract">{{ trade.details }}</span>
                        <span :class="['trade-price', trade.priceChange >= 0 ? 'positive' : 'negative']">
                          {{ trade.price }} <i :class="trade.priceChange >= 0 ? 'fas fa-caret-up' : 'fas fa-caret-down'"></i>
                        </span>
                      </div>
                      <div class="trade-volume">{{ trade.volume || '5 MW' }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>


        <!-- Curves Section -->
        <div class="feature-category mb-5">
          <h3 class="feature-category-title">
            <i class="fas fa-chart-line me-2"></i> {{ t('markets.japanPower.features.curves.title') }}
          </h3>

          <div class="row g-4">
            <!-- Broker EOD Curves -->
            <div class="col-lg-4">
              <div class="ct-card feature-card">
                <div class="feature-header">
                  <div class="feature-icon-wrap">
                    <i class="fas fa-file-alt feature-icon"></i>
                  </div>
                  <h4>{{ t('markets.japanPower.features.curves.brokerEod.title') }}</h4>
                </div>
                <p>{{ t('markets.japanPower.features.curves.brokerEod.description') }}</p>
                <div class="broker-logos">
                  <div class="logos-grid">
                  </div>
                  <div class="curve-sample">
                    <img src="@/assets/img/curves.png" width="100%" />
                  </div>
                </div>
              </div>
            </div>

            <!-- Live Curves -->
            <div class="col-lg-4">
              <div class="ct-card feature-card">
                <div class="feature-header">
                  <div class="feature-icon-wrap">
                    <i class="fas fa-bolt feature-icon"></i>
                  </div>
                  <h4>{{ t('markets.japanPower.features.curves.liveCurves.title') }}</h4>
                  <span class="data-license-badge">{{ t('markets.japanPower.features.curves.liveCurves.badge') }}</span>
                </div>
                <p>{{ t('markets.japanPower.features.curves.liveCurves.description') }}</p>
                <div class="realtime-curves">
                  <div class="curve-controls">
                    <div class="control-group">
                      <label>{{ t('markets.japanPower.features.curves.liveCurves.chart.region') }}</label>
                      <select v-model="selectedRegion" class="curve-select">
                        <option v-for="region in regions" :key="region">{{ region }}</option>
                      </select>
                    </div>
                    <div class="control-group">
                      <label>{{ t('markets.japanPower.features.curves.liveCurves.chart.period') }}</label>
                      <select v-model="selectedPeriod" class="curve-select">
                        <option v-for="period in periods" :key="period">{{ period }}</option>
                      </select>
                    </div>
                  </div>
                  <div class="realtime-chart">
                    <div class="chart-placeholder">
                      <i class="fas fa-chart-line"></i>
                      <span>{{ t('markets.japanPower.features.curves.liveCurves.chart.placeholder') }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Manual Upload -->
            <div class="col-lg-4">
              <div class="ct-card feature-card">
                <div class="feature-header">
                  <div class="feature-icon-wrap">
                    <i class="fas fa-upload feature-icon"></i>
                  </div>
                  <h4>{{ t('markets.japanPower.features.curves.manualUpload.title') }}</h4>
                </div>
                <p>{{ t('markets.japanPower.features.curves.manualUpload.description') }}</p>
                <div class="upload-options">
                  <div class="upload-method">
                    <i class="fas fa-file-excel"></i>
                    <span>{{ t('markets.japanPower.features.curves.manualUpload.methods.excelImport') }}</span>
                  </div>
                  <div class="upload-method">
                    <i class="fas fa-file-csv"></i>
                    <span>{{ t('markets.japanPower.features.curves.manualUpload.methods.csvUpload') }}</span>
                  </div>
                  <div class="upload-method">
                    <i class="fas fa-keyboard"></i>
                    <span>{{ t('markets.japanPower.features.curves.manualUpload.methods.manualEntry') }}</span>
                  </div>
                  <div class="upload-method">
                    <i class="fas fa-history"></i>
                    <span>{{ t('markets.japanPower.features.curves.manualUpload.methods.versionControl') }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- For Traders Section -->
        <div class="feature-category mb-5">
          <h3 class="feature-category-title">
            <i class="fas fa-user-tie me-2"></i> {{ t('markets.japanPower.features.forTraders.title') }}
          </h3>

          <div class="row g-4">
            <!-- Related Markets -->
            <div class="col-lg-6">
              <div class="ct-card feature-card">
                <div class="feature-header">
                  <div class="feature-icon-wrap">
                    <i class="fas fa-project-diagram feature-icon"></i>
                  </div>
                  <h4>{{ t('markets.japanPower.features.forTraders.relatedMarkets.title') }}</h4>
                </div>
                <p>{{ t('markets.japanPower.features.forTraders.relatedMarkets.description') }}</p>
                <div class="related-markets">
                  <div class="market-relationship">
                    <div class="relationship-header">
                      <span>{{ t('markets.japanPower.features.forTraders.relatedMarkets.jkmGas.title') }}</span>
                      <i class="fas fa-arrow-right"></i>
                      <span>{{ t('markets.japanPower.features.forTraders.relatedMarkets.jkmGas.target') }}</span>
                    </div>
                    <div class="relationship-data">
                      <div class="data-point">
                        <span>{{ t('markets.japanPower.features.forTraders.relatedMarkets.jkmGas.data.price.label') }}</span>
                        <span>{{ t('markets.japanPower.features.forTraders.relatedMarkets.jkmGas.data.price.value') }}</span>
                      </div>
                      <div class="data-point">
                        <span>{{ t('markets.japanPower.features.forTraders.relatedMarkets.jkmGas.data.heatRate.label') }}</span>
                        <span>{{ t('markets.japanPower.features.forTraders.relatedMarkets.jkmGas.data.heatRate.value') }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="spark-spread">
                    <div class="spread-header">{{ t('markets.japanPower.features.forTraders.relatedMarkets.sparkSpread.title') }}</div>
                    <div class="spread-data">
                      <div class="spread-formula">
                        <span>{{ t('markets.japanPower.features.forTraders.relatedMarkets.sparkSpread.formula') }}</span>
                      </div>
                      <div class="spread-result">
                        <span>{{ t('markets.japanPower.features.forTraders.relatedMarkets.sparkSpread.result.label') }}</span>
                        <span class="positive">{{ t('markets.japanPower.features.forTraders.relatedMarkets.sparkSpread.result.value') }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Custom Calculations -->
            <div class="col-lg-6">
              <div class="ct-card feature-card">
                <div class="feature-header">
                  <div class="feature-icon-wrap">
                    <i class="fas fa-calculator feature-icon"></i>
                  </div>
                  <h4>{{ t('markets.japanPower.features.forTraders.calculations.title') }}</h4>
                </div>
                <p>{{ t('markets.japanPower.features.forTraders.calculations.description') }}</p>
                <div class="calculation-features">
                  <div class="formula-example">
                    <div class="formula-header">{{ t('markets.japanPower.features.forTraders.calculations.exampleFormula') }}</div>
                    <div class="formula-content">
                      <code>CASE(RECENT({Base Q3 last_traded}, 600), {Base Q3 value})</code>
                    </div>
                  </div>
                  <div class="calc-benefits">
                    <div class="calc-benefit">
                      <i class="fas fa-cogs"></i>
                      <span>{{ t('markets.japanPower.features.forTraders.calculations.benefits.complexFormulas') }}</span>
                    </div>
                    <div class="calc-benefit">
                      <i class="fas fa-lock"></i>
                      <span>{{ t('markets.japanPower.features.forTraders.calculations.benefits.privateModels') }}</span>
                    </div>
                    <div class="calc-benefit">
                      <i class="fas fa-history"></i>
                      <span>{{ t('markets.japanPower.features.forTraders.calculations.benefits.historicalCalcs') }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Internal Curves & Alerts -->
            <div class="col-lg-6">
              <div class="ct-card feature-card">
                <div class="feature-header">
                  <div class="feature-icon-wrap">
                    <i class="fas fa-share-alt feature-icon"></i>
                  </div>
                  <h4>{{ t('markets.japanPower.features.forTraders.sharing.title') }}</h4>
                </div>
                <p>{{ t('markets.japanPower.features.forTraders.sharing.description') }}</p>
                <div class="row">
                  <div class="col-md-6">
                    <div class="sharing-features">
                      <h5>{{ t('markets.japanPower.features.forTraders.sharing.sections.curveSharing.title') }}</h5>
                      <ul class="feature-list">
                        <li>{{ t('markets.japanPower.features.forTraders.sharing.sections.curveSharing.features.rolePermissions') }}</li>
                        <li>{{ t('markets.japanPower.features.forTraders.sharing.sections.curveSharing.features.officeSync') }}</li>
                        <li>{{ t('markets.japanPower.features.forTraders.sharing.sections.curveSharing.features.readWriteControls') }}</li>
                        <li>{{ t('markets.japanPower.features.forTraders.sharing.sections.curveSharing.features.auditTrail') }}</li>
                      </ul>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="alert-features">
                      <h5>{{ t('markets.japanPower.features.forTraders.sharing.sections.priceAlerts.title') }}</h5>
                      <div class="alert-example">
                        <div class="alert-type">{{ t('markets.japanPower.features.forTraders.sharing.sections.priceAlerts.examples.threshold.title') }}</div>
                        <div class="alert-content">{{ t('markets.japanPower.features.forTraders.sharing.sections.priceAlerts.examples.threshold.content') }}</div>
                      </div>
                      <div class="alert-example">
                        <div class="alert-type">{{ t('markets.japanPower.features.forTraders.sharing.sections.priceAlerts.examples.spread.title') }}</div>
                        <div class="alert-content">{{ t('markets.japanPower.features.forTraders.sharing.sections.priceAlerts.examples.spread.content') }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Reports -->
            <div class="col-lg-6">
              <div class="ct-card feature-card">
                <div class="feature-header">
                  <div class="feature-icon-wrap">
                    <i class="fas fa-file-contract feature-icon"></i>
                  </div>
                  <h4>{{ t('markets.japanPower.features.forTraders.reports.title') }}</h4>
                </div>
                <p>{{ t('markets.japanPower.features.forTraders.reports.description') }}</p>
                <div class="report-types">
                  <div class="report-type">
                    <i class="fas fa-chart-pie"></i>
                    <span>{{ t('markets.japanPower.features.forTraders.reports.reportTypes.marketSummary') }}</span>
                  </div>
                  <div class="report-type">
                    <i class="fas fa-balance-scale"></i>
                    <span>{{ t('markets.japanPower.features.forTraders.reports.reportTypes.positionReports') }}</span>
                  </div>
                  <div class="report-type">
                    <i class="fas fa-chart-bar"></i>
                    <span>{{ t('markets.japanPower.features.forTraders.reports.reportTypes.curveAnalysis') }}</span>
                  </div>
                  <div class="report-type">
                    <i class="fas fa-history"></i>
                    <span>{{ t('markets.japanPower.features.forTraders.reports.reportTypes.historicalTrends') }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Back Office STP Section -->
        <div class="feature-category mb-5">
          <h3 class="feature-category-title">
            <i class="fas fa-server me-2"></i> {{ t('markets.japanPower.features.backOffice.title') }}
          </h3>

          <div class="row g-4">
            <div class="col-lg-6">
              <div class="ct-card feature-card">
                <div class="feature-header">
                  <div class="feature-icon-wrap">
                    <i class="fas fa-bolt feature-icon"></i>
                  </div>
                  <h4>{{ t('markets.japanPower.features.backOffice.etrmIntegration.title') }}</h4>
                </div>
                <p>{{ t('markets.japanPower.features.backOffice.etrmIntegration.description') }}</p>

                <div class="etrm-integration">
                  <div class="integration-flow">
                    <div class="flow-step">
                      <div class="flow-icon-wrap">
                        <i class="fas fa-handshake"></i>
                      </div>
                      <div class="flow-description">{{ t('markets.japanPower.features.backOffice.etrmIntegration.flow.executed') }}</div>
                    </div>
                    <div class="flow-arrow"><i class="fas fa-long-arrow-alt-right"></i></div>
                    <div class="flow-step">
                      <div class="flow-icon-wrap">
                        <i class="fas fa-bolt"></i>
                      </div>
                      <div class="flow-description">{{ t('markets.japanPower.features.backOffice.etrmIntegration.flow.websocket') }}</div>
                    </div>
                    <div class="flow-arrow"><i class="fas fa-long-arrow-alt-right"></i></div>
                    <div class="flow-step">
                      <div class="flow-icon-wrap">
                        <i class="fas fa-database"></i>
                      </div>
                      <div class="flow-description">{{ t('markets.japanPower.features.backOffice.etrmIntegration.flow.etrm') }}</div>
                    </div>
                  </div>

                  <div class="supported-systems">
                    <div class="system-badge">{{ t('markets.japanPower.features.backOffice.etrmIntegration.supportedSystems.endur') }}</div>
                    <div class="system-badge">{{ t('markets.japanPower.features.backOffice.etrmIntegration.supportedSystems.allegro') }}</div>
                    <div class="system-badge">{{ t('markets.japanPower.features.backOffice.etrmIntegration.supportedSystems.triplePoint') }}</div>
                    <div class="system-badge">{{ t('markets.japanPower.features.backOffice.etrmIntegration.supportedSystems.openlink') }}</div>
                    <div class="system-badge">{{ t('markets.japanPower.features.backOffice.etrmIntegration.supportedSystems.customApi') }}</div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-lg-6">
              <div class="ct-card feature-card">
                <div class="feature-header">
                  <div class="feature-icon-wrap">
                    <i class="fas fa-code feature-icon"></i>
                  </div>
                  <h4>{{ t('markets.japanPower.features.backOffice.apiExample.title') }}</h4>
                </div>
                <p>{{ t('markets.japanPower.features.backOffice.apiExample.description') }}</p>

                <div class="api-example">
                  <div class="code-header">
                    <span>WebSocket Trade Event</span>
                    <div class="code-badges">
                      <span class="code-badge">{{ t('markets.japanPower.features.backOffice.apiExample.codeBadges.json') }}</span>
                      <span class="code-badge">{{ t('markets.japanPower.features.backOffice.apiExample.codeBadges.realtime') }}</span>
                    </div>
                  </div>
                  <div class="code-block">
        <pre><code>{
          "event": "trade",
          "timestamp": "2025-05-16T09:23:42.125Z",
          "trade_id": "CT-JP245789",
          "data": {
            "product": "Tokyo Base Q3-25",
            "price": 16.75,
            "quantity": 5,
            "unit": "MW",
            "buy_party": "Trader A",
            "sell_party": "Trader B",
            "trade_date": "2025-05-16",
            "settlement": "financial",
            "broker": "Vanir Energy",
            "clearing": "EEX"
          }
        }</code></pre>
                  </div>
                  <div class="code-features">
                    <div class="code-feature">
                      <i class="fas fa-check-circle"></i>
                      <span>{{ t('markets.japanPower.features.backOffice.apiExample.features.tradeDetails') }}</span>
                    </div>
                    <div class="code-feature">
                      <i class="fas fa-check-circle"></i>
                      <span>{{ t('markets.japanPower.features.backOffice.apiExample.features.timestamps') }}</span>
                    </div>
                    <div class="code-feature">
                      <i class="fas fa-check-circle"></i>
                      <span>{{ t('markets.japanPower.features.backOffice.apiExample.features.secureProtocol') }}</span>
                    </div>
                    <div class="code-feature">
                      <i class="fas fa-check-circle"></i>
                      <span>{{ t('markets.japanPower.features.backOffice.apiExample.features.filters') }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Benefits Row -->
            <div class="col-12">
              <div class="ct-card backoffice-benefits-card">
                <h4><i class="fas fa-star me-2"></i> {{ t('markets.japanPower.features.backOffice.benefits.title') }}</h4>
                <div class="benefits-grid">
                  <div class="benefit-item">
                    <div class="benefit-icon"><i class="fas fa-tachometer-alt"></i></div>
                    <div class="benefit-title">{{ t('markets.japanPower.features.backOffice.benefits.realTime.title') }}</div>
                    <div class="benefit-description">{{ t('markets.japanPower.features.backOffice.benefits.realTime.description') }}</div>
                  </div>
                  <div class="benefit-item">
                    <div class="benefit-icon"><i class="fas fa-shield-alt"></i></div>
                    <div class="benefit-title">{{ t('markets.japanPower.features.backOffice.benefits.reducedRisk.title') }}</div>
                    <div class="benefit-description">{{ t('markets.japanPower.features.backOffice.benefits.reducedRisk.description') }}</div>
                  </div>
                  <div class="benefit-item">
                    <div class="benefit-icon"><i class="fas fa-chart-line"></i></div>
                    <div class="benefit-title">{{ t('markets.japanPower.features.backOffice.benefits.efficiency.title') }}</div>
                    <div class="benefit-description">{{ t('markets.japanPower.features.backOffice.benefits.efficiency.description') }}</div>
                  </div>
                  <div class="benefit-item">
                    <div class="benefit-icon"><i class="fas fa-plug"></i></div>
                    <div class="benefit-title">{{ t('markets.japanPower.features.backOffice.benefits.integration.title') }}</div>
                    <div class="benefit-description">{{ t('markets.japanPower.features.backOffice.benefits.integration.description') }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Clearing Section -->
        <div class="feature-category mb-5">
          <h3 class="feature-category-title">
            <i class="fas fa-sync-alt me-2"></i> {{ t('markets.japanPower.features.clearing.title') }}
          </h3>

          <div class="row g-4">
            <!-- EEX Clearing STP -->
            <div class="col-lg-6">
              <div class="ct-card feature-card">
                <div class="feature-header">
                  <div class="feature-icon-wrap">
                    <i class="fas fa-exchange-alt feature-icon"></i>
                  </div>
                  <h4>{{ t('markets.japanPower.features.clearing.eex.title') }}</h4>
                </div>
                <p>{{ t('markets.japanPower.features.clearing.eex.description') }}</p>
                <div class="stp-workflow">
                  <div class="workflow-step">
                    <div class="step-icon"><i class="fas fa-handshake"></i></div>
                    <div class="step-label">{{ t('markets.japanPower.features.clearing.eex.workflow.execution') }}</div>
                  </div>
                  <div class="workflow-arrow"><i class="fas fa-arrow-right"></i></div>
                  <div class="workflow-step">
                    <div class="step-icon"><i class="fas fa-sync"></i></div>
                    <div class="step-label">{{ t('markets.japanPower.features.clearing.eex.workflow.stp') }}</div>
                  </div>
                  <div class="workflow-arrow"><i class="fas fa-arrow-right"></i></div>
                  <div class="workflow-step">
                    <div class="step-icon"><i class="fas fa-check-circle"></i></div>
                    <div class="step-label">{{ t('markets.japanPower.features.clearing.eex.workflow.clearing') }}</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- TOCOM Clearing STP -->
            <div class="col-lg-6">
              <div class="ct-card feature-card">
                <div class="feature-header">
                  <div class="feature-icon-wrap">
                    <i class="fas fa-sync feature-icon"></i>
                  </div>
                  <h4>{{ t('markets.japanPower.features.clearing.tocom.title') }}</h4>
                </div>
                <p>{{ t('markets.japanPower.features.clearing.tocom.description') }}</p>
                <div class="broker-benefits">
                  <div class="benefit-item">
                    <i class="fas fa-clock"></i>
                    <span>{{ t('markets.japanPower.features.clearing.tocom.benefits.processingTime') }}</span>
                  </div>
                  <div class="benefit-item">
                    <i class="fas fa-shield-alt"></i>
                    <span>{{ t('markets.japanPower.features.clearing.tocom.benefits.operationalRisk') }}</span>
                  </div>
                  <div class="benefit-item">
                    <i class="fas fa-chart-bar"></i>
                    <span>{{ t('markets.japanPower.features.clearing.tocom.benefits.statusUpdates') }}</span>
                  </div>
                  <div class="benefit-item">
                    <i class="fas fa-file-invoice"></i>
                    <span>{{ t('markets.japanPower.features.clearing.tocom.benefits.reporting') }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>


        <!-- Integration Options -->
        <div class="feature-category">
          <h3 class="feature-category-title">
            <i class="fas fa-plug me-2"></i> {{ t('markets.japanPower.features.integration.title') }}
          </h3>

          <div class="ct-card integration-card">
            <div class="row align-items-center">
              <div class="col-md-8">
                <h4>{{ t('markets.japanPower.features.integration.subtitle') }}</h4>
                <div class="integration-options">
                  <div class="integration-option">
                    <i class="fas fa-file-excel"></i>
                    <span>{{ t('markets.japanPower.features.integration.options.excel.title') }}</span>
                    <p>{{ t('markets.japanPower.features.integration.options.excel.description') }}</p>
                  </div>
                  <div class="integration-option">
                    <i class="fas fa-mobile-alt"></i>
                    <span>{{ t('markets.japanPower.features.integration.options.mobile.title') }}</span>
                    <p>{{ t('markets.japanPower.features.integration.options.mobile.description') }}</p>
                  </div>
                  <div class="integration-option">
                    <i class="fas fa-code"></i>
                    <span>{{ t('markets.japanPower.features.integration.options.api.title') }}</span>
                    <p>{{ t('markets.japanPower.features.integration.options.api.description') }}</p>
                  </div>
                </div>
              </div>
              <div class="col-md-4 text-center">
                <img src="@/assets/img/excelmobile.png" alt="Integration Preview" class="img-fluid integration-preview">
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Call to Action -->
    <section class="ct-section ct-dark-section">
      <div class="container text-center">
        <h2 class="ct-title mb-4">{{ t('markets.japanPower.cta.title') }}</h2>
        <p class="lead mb-5">{{ t('markets.japanPower.cta.subtitle') }}</p>
        <router-link to="/contact" class="ct-btn-primary">{{ t('markets.japanPower.cta.button') }}</router-link>
      </div>
    </section>
  </div>
</template>

<script>
import JapanMap from '@/components/JapanMap.vue';
import { useI18n } from 'vue-i18n';

export default {
  name: 'JapanPower',
  components: {
    JapanMap
  },
  setup() {
    const { t, locale } = useI18n();
    return { t, locale };
  },
  data() {
    return {
      currentTime: this.formatTime(new Date()),
      timeInterval: null,
      activeRegion: "tokyo",

      // Region mapping for financial markets
      financialRegionMap: {
        'financial-tokyo': 'tokyo',
        'financial-kansai': 'kansai'
      },

      // Region mappings for highlighting
      regionMappings: {
        'tokyo': ['tepco'],
        'kansai': ['kansai'],
        'financial-tokyo': ['tepco'],
        'financial-kansai': ['kansai']
      },

      // Standard region data
     regionData: {
        tokyo: {
          name: 'Tokyo (TEPCO)',
          basePrice: '¥12.45/kWh',
          peakPrice: '¥15.70/kWh',
          generationMix: 'LNG 60%, Coal 20%, Oil 2%, Nuclear 0%, Renewables 18%',  // FY2022
          frequency: '50Hz'
        },
        kansai: {
          name: 'Kansai (KEPCO)',
          basePrice: '¥11.20/kWh',
          peakPrice: '¥14.30/kWh',
          generationMix: 'LNG 22%, Coal 20%, Oil 3%, Nuclear 20%, Renewables 15%',  // FY2022
          frequency: '60Hz'
        },
        kyushu: {
          name: 'Kyushu Electric',
          basePrice: '¥10.80/kWh',
          peakPrice: '¥13.50/kWh',
          generationMix: 'Coal 29%, LNG 19%, Oil 1%, Nuclear 23%, Renewables 28%',   // FY2022
          frequency: '60Hz'
        },
        tohoku: {
          name: 'Tohoku Electric',
          basePrice: '¥11.60/kWh',
          peakPrice: '¥14.80/kWh',
          generationMix: 'Coal 27%, LNG 26%, Oil 2%, Nuclear 0%, Renewables 19%',    // FY2022
          frequency: '50Hz'
        },
        hokkaido: {
          name: 'Hokkaido Electric',
          basePrice: '¥11.85/kWh',
          peakPrice: '¥15.10/kWh',
          generationMix: 'Coal 34%, Oil 14%, LNG 8%, Nuclear 0%, Renewables 20%',    // FY2022
          frequency: '50Hz'
        },
        hokuriku: {
          name: 'Hokuriku Electric',
          basePrice: '¥10.95/kWh',
          peakPrice: '¥13.80/kWh',
          generationMix: 'Coal 48%, LNG 8%, Oil 4%, Nuclear 0%, Renewables 32%',    // FY2022
          frequency: '60Hz'
        },
        chubu: {
          name: 'Chubu Electric',
          basePrice: '¥11.15/kWh',
          peakPrice: '¥14.60/kWh',
          generationMix: 'LNG 42%, Coal 22%, Oil 1%, Nuclear 0%, Renewables 16%',   // FY2022
          frequency: '60Hz'
        },
        chugoku: {
          name: 'Chugoku Electric',
          basePrice: '¥11.30/kWh',
          peakPrice: '¥14.25/kWh',
          generationMix: 'Coal 38%, LNG 15%, Oil 4%, Nuclear 0%, Renewables 7%',    // FY2022 estimate
          frequency: '60Hz'
        },
        shikoku: {
          name: 'Shikoku Electric',
          basePrice: '¥11.05/kWh',
          peakPrice: '¥14.10/kWh',
          generationMix: 'Coal 22%, LNG 7%, Oil 7%, Nuclear 20%, Renewables 18%',   // FY2022
          frequency: '60Hz'
        },
        okinawa: {
          name: 'Okinawa Electric',
          basePrice: '¥15.80/kWh',
          peakPrice: '¥18.90/kWh',
          generationMix: 'Oil 70%, LNG 25%, Renewables 5%',                        // FY2022 estimate
          frequency: '60Hz'
        },
        tepco: {
          name: 'Tokyo (TEPCO)',
          basePrice: '¥12.45/kWh',
          peakPrice: '¥15.70/kWh',
          generationMix: 'LNG 60%, Coal 20%, Oil 2%, Nuclear 0%, Renewables 18%',  // FY2022
          frequency: '50Hz'
        }
      },


      // Financial market specific data
      financialRegionData: {
        'financial-tokyo': {
          name: 'Tokyo Financial Market',
          basePrice: '¥12.45/kWh',
          peakPrice: '¥15.70/kWh',
          generationMix: 'LNG 60%, Coal 20%, Oil 2%, Nuclear 0%, Renewables 18%',
          frequency: '50Hz',
          tradingVolume: '450 MW/day',
          openInterest: '1,250 contracts'
        },
        'financial-kansai': {
          name: 'Kansai Financial Market',
          basePrice: '¥11.20/kWh',
          peakPrice: '¥14.30/kWh',
          generationMix: 'LNG 22%, Coal 20%, Oil 3%, Nuclear 20%, Renewables 15%',
          frequency: '60Hz',
          tradingVolume: '275 MW/day',
          openInterest: '850 contracts'
        }
      },

      recentTrades: [
        { time: '14:23:01', details: 'Tokyo BASE Jun-25', price: '¥12.00', priceChange: 0.35, volume: '5 MWh' },
        { time: '14:21:45', details: 'Kansai BASE Win-26', price: '¥11.45', priceChange: -0.50, volume: '4 MW' },
        { time: '14:20:12', details: 'Tokyo PEAK Sep-25', price: '¥16.70', priceChange: 0.25, volume: '10 MW' },
        { time: '14:17:55', details: 'Kansai PEAK Q4-25', price: '¥12.30', priceChange: 0.80, volume: '5 MW' }
      ],
      selectedRegion: 'Tokyo',
      regions: ['Tokyo', 'Kansai'],
      selectedPeriod: 'Months',
      periods: ['Weeks', 'Months', 'Quarters', 'Seasons', 'Fin Years']
    };
  },
  computed: {
    activeRegionData() {
      // Handle financial regions by using financial-specific data
      if (this.activeRegion && this.activeRegion.startsWith('financial-')) {
        // Use financial-specific data if available
        if (this.financialRegionData[this.activeRegion]) {
          return this.financialRegionData[this.activeRegion];
        }
        // Fall back to base region if needed
        const baseRegion = this.financialRegionMap[this.activeRegion];
        return this.regionData[baseRegion];
      }

      // Regular region
      return this.regionData[this.activeRegion];
    }
  },
  mounted() {
    this.timeInterval = setInterval(() => {
      this.currentTime = this.formatTime(new Date());
    }, 1000);

    this.setPageTitle(this.t('markets.japanPower.pageTitle'));
    this.$nextTick(() => {
      this.setupTabSwitching();
    });
  },
  beforeUnmount() {
    // Clear interval when component is destroyed
    clearInterval(this.timeInterval);
  },
  methods: {
    setPageTitle(title) {
      document.title = title;
    },
    formatTime(date) {
      return date.toLocaleTimeString('ja-JP', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    },
    setupTabSwitching() {
      // Get all tab headers
      const tabItems = document.querySelectorAll('.tab-item');

      // Add click event to each tab header
      tabItems.forEach(tab => {
        tab.addEventListener('click', () => {
          // Remove active class from all tabs
          tabItems.forEach(t => t.classList.remove('active'));

          // Add active class to clicked tab
          tab.classList.add('active');

          // Get the tab id to show
          const tabId = tab.getAttribute('data-tab');

          // Hide all tab panes
          document.querySelectorAll('.tab-pane').forEach(pane => {
            pane.classList.remove('active');
          });

          // Show the selected tab pane
          document.getElementById(`${tabId}-tab`).classList.add('active');
        });
      });
    },
    highlightRegion(region) {
      this.activeRegion = region;

      // Get the regions to highlight
      const regionsToHighlight = this.regionMappings[region] || [region];

      // Call the child component's method
      if (this.$refs.japanMap) {
        this.$refs.japanMap.highlightRegion(regionsToHighlight);
      }
    },
    resetHighlight() {
      if (this.$refs.japanMap) {
        this.$refs.japanMap.resetHighlight();
      }
    },
    getRegionName(region) {
      if (region.startsWith('financial-')) {
        const baseRegion = region.replace('financial-', '');
        return this.t(`markets.japanPower.regionData.regions.financial.${baseRegion}.name`);
      }
      return this.t(`markets.japanPower.regionData.regions.${region}.name`);
    },

    getRegionData(region, property) {
      if (region.startsWith('financial-')) {
        const baseRegion = region.replace('financial-', '');
        return this.t(`markets.japanPower.regionData.regions.financial.${baseRegion}.${property}`);
      }
      return this.t(`markets.japanPower.regionData.regions.${region}.${property}`);
    },

    isFinancialRegion(region) {
      return region.startsWith('financial-');
    }
  }
};
</script>

<style scoped>
/* Hero Section Styles */
.japan-power-hero {
  background-image: linear-gradient(to right, rgba(18, 20, 23, 0.8), rgba(18, 20, 23, 0.9)),
    url('@/assets/img/placeholder-japan-power.jpg');
  background-size: cover;
  background-position: center;
}

.market-stats-card {
  background-color: var(--ct-dark-tertiary);
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.market-stats-card h3 {
  color: var(--ct-orange);
  margin-bottom: 1.25rem;
  font-size: 1.5rem;
}

.market-stat-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 1.25rem;
}

.market-stat-row:last-child {
  margin-bottom: 0;
}

.market-stat {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.stat-label {
  font-size: 0.85rem;
  color: var(--ct-light-gray);
  margin-bottom: 0.25rem;
}

.stat-value {
  font-size: 1.25rem;
  font-weight: 600;
  color: #fff;
}

.stat-change {
  font-size: 0.9rem;
  font-weight: 500;
  margin-top: 0.15rem;
}

.positive {
  color: #4caf50;
}

.negative {
  color: #f44336;
}

/* Feature Category Styles */
.feature-category {
  margin-bottom: 3rem;
}

.feature-category-title {
  color: var(--ct-orange);
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

/* Feature Card Styles */
.feature-card {
  height: 100%;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  overflow: hidden;
}

.feature-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.4);
}

.feature-header {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  position: relative;
}

.feature-icon-wrap {
  width: 40px;
  height: 40px;
  background-color: rgba(253, 153, 0, 0.1);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
}

.feature-icon {
  color: var(--ct-orange);
  font-size: 1.25rem;
}

.feature-header h4 {
  margin: 0;
  font-size: 1.25rem;
  color: #fff;
}

.data-license-badge {
  background-color: var(--ct-orange);
  color: #000;
  font-size: 0.65rem;
  font-weight: 600;
  padding: 0.15rem 0.4rem;
  border-radius: 4px;
  margin-left: 0.75rem;
  text-transform: uppercase;
  white-space: normal;
  text-align: center;
  line-height: 1.1;
  max-width: 80px;
}

/* CLOB Preview Styles */
/* Updated CLOB Preview Styles */
.clob-preview {
  background-color: var(--ct-dark);
  border-radius: 6px;
  margin-top: 1rem;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.clob-header {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr;
  background-color: rgba(255, 255, 255, 0.05);
  padding: 0.5rem 0.75rem;
  font-size: 0.8rem;
  color: var(--ct-light-gray);
  font-weight: 600;
  text-align: center;
}

.contract-header {
  text-align: left;
}

.clob-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr;
  padding: 0.6rem 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  text-align: center;
}

.clob-row:last-child {
  border-bottom: none;
}

.contract-name {
  font-size: 0.9rem;
  color: #fff;
  text-align: left;
}

.bid-qty, .bid-price {
  background-color: #00283F;
  border-radius: 3px;
  margin: 0 1px;
  padding: 3px 0;
}

.ask-qty, .ask-price {
  background-color: #3F1717;
  border-radius: 3px;
  margin: 0 1px;
  padding: 3px 0;
}

.bid-price, .ask-price {
  color: #FF8843;
  font-weight: 600;
}

.bid-qty, .ask-qty {
  color: #fff;
  font-size: 0.85rem;
}

/* Settlement Preview Styles */
.settlement-preview {
  background-color: var(--ct-dark);
  border-radius: 6px;
  margin-top: 1rem;
  overflow: hidden;
}

.exchange-tabs {
  display: flex;
  background-color: rgba(255, 255, 255, 0.05);
}

.exchange-tabs span {
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  cursor: pointer;
}

.exchange-tabs span.active {
  background-color: rgba(253, 153, 0, 0.1);
  color: var(--ct-orange);
  font-weight: 600;
}

.settlement-table {
  padding: 0.5rem;
}

.table-header {
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr;
  padding: 0.5rem 0.75rem;
  font-size: 0.8rem;
  color: var(--ct-light-gray);
  font-weight: 600;
}

.table-row {
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr;
  padding: 0.6rem 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.table-row:last-child {
  border-bottom: none;
}

.table-row span {
  font-size: 0.9rem;
  color: #fff;
}

.table-row span.positive {
  color: #4caf50;
}

.table-row span.negative {
  color: #f44336;
}

/* Recent Trades Styles */
.recent-trades {
  margin-top: 1rem;
}

.trades-scrollable {
  max-height: 200px;
  overflow-y: auto;
  background-color: var(--ct-dark);
  border-radius: 6px;
}

.trade-item {
  padding: 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.trade-item:last-child {
  border-bottom: none;
}

.trade-time {
  font-size: 0.75rem;
  color: var(--ct-light-gray);
  margin-bottom: 0.25rem;
}

.trade-details {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.25rem;
}

.trade-contract {
  font-size: 0.9rem;
  color: #fff;
}

.trade-price {
  font-size: 0.9rem;
  font-weight: 600;
}

.trade-volume {
  font-size: 0.8rem;
  color: var(--ct-light-gray);
}

/* STP Workflow Styles */
.stp-workflow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 1.5rem;
}

.workflow-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.step-icon {
  width: 50px;
  height: 50px;
  background-color: rgba(253, 153, 0, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--ct-orange);
  font-size: 1.25rem;
  margin-bottom: 0.5rem;
}

.step-label {
  font-size: 0.85rem;
  color: #fff;
}

.workflow-arrow {
  color: var(--ct-orange);
  font-size: 1.25rem;
}

/* Broker Benefits Styles */
.broker-benefits {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-top: 1.5rem;
}

.benefit-item {
  //display: flex;
  align-items: center;
  background-color: var(--ct-dark);
  padding: 0.75rem;
  border-radius: 6px;
}

.benefit-item i {
  color: var(--ct-orange);
  font-size: 1.25rem;
  //margin-right: 0.75rem;
}

.benefit-item span {
  font-size: 0.85rem;
  color: #fff;
}

/* Broker Logos Styles */
.broker-logos {
  margin-top: 1rem;
}

.logos-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.broker-logo {
  background-color: var(--ct-dark);
  color: #fff;
  padding: 0.5rem;
  border-radius: 4px;
  font-size: 0.9rem;
  text-align: center;
}

.curve-sample {
  margin-top: 1rem;
}

.chart-placeholder {
  background-color: var(--ct-dark);
  border-radius: 6px;
  height: 120px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--ct-light-gray);
}

.chart-placeholder i {
  font-size: 2rem;
  margin-bottom: 0.5rem;
  color: var(--ct-orange);
}

/* Realtime Curves Styles */
.curve-controls {
  display: flex;
  margin-bottom: 1rem;
  gap: 1rem;
}

.control-group {
  display: flex;
  align-items: center;
}

.control-group label {
  margin-right: 0.5rem;
  margin-bottom: 0;
  color: var(--ct-light-gray);
  font-size: 0.85rem;
}

.curve-select {
  background-color: var(--ct-dark);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  padding: 0.3rem 0.5rem;
  font-size: 0.85rem;
}

.realtime-chart {
  height: 150px;
}

/* Upload Options Styles */
.upload-options {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
  margin-top: 1rem;
}

.upload-method {
  background-color: var(--ct-dark);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem 0.5rem;
  border-radius: 6px;
}

.upload-method i {
  font-size: 1.5rem;
  color: var(--ct-orange);
  margin-bottom: 0.5rem;
}

.upload-method span {
  font-size: 0.85rem;
  color: #fff;
  text-align: center;
}

/* Calculation Features Styles */
.calculation-features {
  margin-top: 1rem;
}

.formula-example {
  background-color: var(--ct-dark);
  border-radius: 6px;
  padding: 0.75rem;
  margin-bottom: 1rem;
}

.formula-header {
  font-size: 0.85rem;
  color: var(--ct-light-gray);
  margin-bottom: 0.5rem;
}

.formula-content {
  background-color: rgba(0, 0, 0, 0.3);
  padding: 0.5rem;
  border-radius: 4px;
  overflow-x: auto;
}

.formula-content code {
  color: #fff;
  font-size: 0.85rem;
}

.calc-benefits {
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 0.75rem;
}

.calc-benefit {
  display: flex;
  align-items: center;
  background-color: var(--ct-dark);
  padding: 0.6rem 0.75rem;
  border-radius: 6px;
}

.calc-benefit i {
  color: var(--ct-orange);
  font-size: 1rem;
  margin-right: 0.75rem;
  width: 16px;
  text-align: center;
}

.calc-benefit span {
  font-size: 0.85rem;
  color: #fff;
}

/* Sharing & Alert Features Styles */
.sharing-features, .alert-features {
  margin-top: 1rem;
}

.sharing-features h5, .alert-features h5 {
  font-size: 1rem;
  color: var(--ct-orange);
  margin-bottom: 0.75rem;
}

.feature-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.feature-list li {
  position: relative;
  padding-left: 1.5rem;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  color: #fff;
}

.feature-list li::before {
  content: "\f00c";
  font-family: "Font Awesome 5 Free";
  font-weight: 900;
  position: absolute;
  left: 0;
  color: var(--ct-orange);
}

.alert-example {
  background-color: var(--ct-dark);
  border-radius: 6px;
  padding: 0.75rem;
  margin-bottom: 0.75rem;
}

.alert-type {
  font-size: 0.8rem;
  color: var(--ct-light-gray);
  margin-bottom: 0.25rem;
}

.alert-content {
  font-size: 0.85rem;
  color: #fff;
}

/* Report Types Styles */
.report-types {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
  margin-top: 1rem;
}

.report-type {
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: var(--ct-dark);
  padding: 1rem 0.75rem;
  border-radius: 6px;
  text-align: center;
}

.report-type i {
  font-size: 1.5rem;
  color: var(--ct-orange);
  margin-bottom: 0.75rem;
}

.report-type span {
  font-size: 0.85rem;
  color: #fff;
}

/* Related Markets Styles */
.related-markets {
  margin-top: 1rem;
}

.market-relationship {
  background-color: var(--ct-dark);
  border-radius: 6px;
  padding: 0.75rem;
  margin-bottom: 1rem;
}

.relationship-header {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  color: #fff;
  margin-bottom: 0.75rem;
}

.relationship-header i {
  color: var(--ct-orange);
  margin: 0 0.75rem;
}

.relationship-data {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
}

.data-point {
  text-align: center;
}

.data-point span:first-child {
  display: block;
  font-size: 0.8rem;
  color: var(--ct-light-gray);
  margin-bottom: 0.25rem;
}

.data-point span:last-child {
  font-size: 0.9rem;
  color: #fff;
  font-weight: 500;
}

.spark-spread {
  background-color: var(--ct-dark);
  border-radius: 6px;
  padding: 0.75rem;
}

.spread-header {
  font-size: 0.9rem;
  color: #fff;
  text-align: center;
  margin-bottom: 0.75rem;
}

.spread-formula {
  background-color: rgba(0, 0, 0, 0.3);
  padding: 0.5rem;
  border-radius: 4px;
  text-align: center;
  margin-bottom: 0.75rem;
  font-size: 0.85rem;
  color: var(--ct-light-gray);
}

.spread-result {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.spread-result span:first-child {
  font-size: 0.85rem;
  color: var(--ct-light-gray);
}

.spread-result span:last-child {
  font-size: 0.9rem;
  font-weight: 600;
}

/* Integration Options Styles */
.integration-card {
  padding: 1.5rem;
}

.integration-options {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin-top: 1.5rem;
}

.integration-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 1.5rem;
  background-color: var(--ct-dark-tertiary);
  border-radius: 8px;
  transition: transform 0.3s ease;
}

.integration-option:hover {
  transform: translateY(-5px);
}

.integration-option i {
  font-size: 2rem;
  color: var(--ct-orange);
  margin-bottom: 1rem;
}

.integration-option span {
  font-size: 1.1rem;
  font-weight: 600;
  color: #fff;
  margin-bottom: 0.75rem;
}

.integration-option p {
  font-size: 0.85rem;
  color: var(--ct-light-gray);
  margin: 0;
}

.integration-preview {
  max-height: 300px;
  filter: drop-shadow(0 10px 20px rgba(0, 0, 0, 0.3));
}

/* Japan Map Section Styles */
.japan-map-section {
  background-color: var(--ct-dark-tertiary);
  padding: 4rem 0;
}

.japan-map-container {
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.japan-map {
  width: 100%;
  max-height: 500px;
  filter: drop-shadow(0 10px 20px rgba(0, 0, 0, 0.3));
}

.map-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.region-data-box {
  position: absolute;
  left: 75px;
  top: 10px;
  background-color: var(--ct-dark);
  border-radius: 8px;
  padding: 0.5rem;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.4);
  border: 1px solid var(--ct-orange);
  max-width: 280px;
}

.region-data-box h4 {
  color: var(--ct-orange);
  margin-bottom: 1rem;
  font-size: 1.1rem;
}

.region-data-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.75rem;
  font-size: 0.9rem;
}

.region-data-row:last-child {
  margin-bottom: 0;
}

.region-data-row span:first-child {
  color: var(--ct-light-gray);
}

.region-data-row span:last-child {
  color: #fff;
  font-weight: 500;
}

.map-info-content {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

/* Market Category Styles */
.market-category {
  margin-bottom: 1.5rem;
}

.market-category-title {
  color: var(--ct-orange);
  font-size: 1.3rem;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
}

/* Region Card Styles */
.region-cards {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.region-card {
  background-color: var(--ct-dark);
  border-radius: 8px;
  padding: 1rem;
  cursor: pointer;
  position: relative;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  flex: 1;
  min-width: 120px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.region-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
}

.region-card h4 {
  font-size: 1rem;
  margin-bottom: 0.4rem;
  color: #fff;
}

.region-card p {
  font-size: 0.8rem;
  color: var(--ct-light-gray);
  margin: 0;
}

.region-card-badge {
  position: absolute;
  top: -8px;
  right: -8px;
  background-color: var(--ct-orange);
  color: #000;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
}

.region-card.financial {
  border-top: 3px solid var(--ct-orange);
}

.region-card.hz50 {
  border-top: 3px solid #4f7dc9;
}

.region-card.hz60 {
  border-top: 3px solid #c94f4f;
}

/* Frequency Tabs Styles */
.frequency-tabs {
  background-color: var(--ct-dark);
  border-radius: 8px;
  overflow: hidden;
  margin-top: 1rem;
}

.tab-header {
  display: flex;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.tab-item {
  padding: 0.8rem 1.2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: background-color 0.2s;
}

.tab-item.active {
  background-color: rgba(255, 255, 255, 0.05);
}

.frequency-chip {
  display: inline-block;
  width: 36px;
  height: 18px;
  line-height: 18px;
  text-align: center;
  border-radius: 9px;
  font-size: 0.7rem;
  font-weight: 600;
}

.tab-item:first-child .frequency-chip {
  background-color: #4f7dc9;
  color: white;
}

.tab-item:nth-child(2) .frequency-chip {
  background-color: #c94f4f;
  color: white;
}

.tab-content {
  padding: 1.2rem;
}

.tab-pane {
  display: none;
}

.tab-pane.active {
  display: block;
}

.region-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 0.8rem;
}

/* Back Office STP Section Styles */
.etrm-integration {
  margin-top: 1.5rem;
}

.integration-flow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: var(--ct-dark);
  border-radius: 8px;
  padding: 1.5rem 1rem;
  margin-bottom: 1.25rem;
}

.flow-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.flow-icon-wrap {
  width: 48px;
  height: 48px;
  background-color: rgba(253, 153, 0, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.75rem;
}

.flow-icon-wrap i {
  color: var(--ct-orange);
  font-size: 1.25rem;
}

.flow-description {
  font-size: 0.85rem;
  color: #fff;
  max-width: 90px;
  text-align: center;
}

.flow-arrow {
  color: var(--ct-orange);
  font-size: 1.5rem;
}

.supported-systems {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
}

.system-badge {
  background-color: var(--ct-dark);
  color: #fff;
  padding: 0.4rem 0.8rem;
  border-radius: 4px;
  font-size: 0.85rem;
  border: 1px solid rgba(253, 153, 0, 0.3);
}

/* API Example Styles */
.api-example {
  margin-top: 1.5rem;
}

.code-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.3);
  padding: 0.5rem 0.75rem;
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-bottom: none;
}

.code-header span {
  font-size: 0.9rem;
  color: #fff;
}

.code-badges {
  display: flex;
  gap: 0.5rem;
}

.code-badge {
  background-color: var(--ct-orange);
  color: #000;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.1rem 0.5rem;
  border-radius: 3px;
}

.code-block {
  background-color: var(--ct-dark);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-bottom-left-radius: 6px;
  border-bottom-right-radius: 6px;
  padding: 0.5rem;
  overflow: auto;
  max-height: 250px;
}

.code-block pre {
  margin: 0;
  color: #e0e0e0;
  font-size: 0.85rem;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
}

.code-block code {
  display: block;
  white-space: pre;
  color: #e0e0e0;
}

.code-features {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
  margin-top: 1rem;
}

.code-feature {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.code-feature i {
  color: var(--ct-orange);
  font-size: 0.85rem;
}

.code-feature span {
  font-size: 0.85rem;
  color: #fff;
}

/* Benefits Card Styles */
.backoffice-benefits-card {
  padding: 1.5rem;
  margin-top: 1rem;
}

.backoffice-benefits-card h4 {
  color: #fff;
  margin-bottom: 1.5rem;
  font-size: 1.3rem;
}

.backoffice-benefits-card h4 i {
  color: var(--ct-orange);
}

.benefits-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
}

.benefit-icon {
  width: 60px;
  height: 60px;
  background-color: rgba(253, 153, 0, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
}

.benefit-icon i {
  color: var(--ct-orange);
  font-size: 1.5rem;
}

.benefit-title {
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  margin-bottom: 0.75rem;
}

.benefit-description {
  font-size: 0.85rem;
  color: var(--ct-light-gray);
  line-height: 1.5;
}

.curve-sample img {
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  position: relative;
  display: block;
  max-width: 100%;
  height: auto;
  margin-top: 0.25rem;
}

/* Vignette effect using pseudo-element */
.curve-sample {
  position: relative;
  overflow: hidden;
  border-radius: 8px;
}

.curve-sample::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  box-shadow: inset 0 0 50px rgba(0, 0, 0, 0.8);
  pointer-events: none;
  border-radius: 8px;
}

/* Add a subtle border */
.curve-sample {
  border: 1px solid rgba(255, 255, 255, 0.05);
  background-color: var(--ct-dark);
  padding: 1rem;
  border-radius: 8px;
}

/* Responsive adjustments */
@media (max-width: 992px) {
  .benefits-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .integration-flow {
    flex-direction: column;
    gap: 1rem;
  }

  .flow-arrow {
    transform: rotate(90deg);
  }

  .market-stats-card {
    margin-top: 1rem;
  }

  /* Reduce padding for better mobile spacing */
  .ct-hero-section {
    padding: 5rem 0 3rem;
  }

  /* Make the text more suitable for mobile */
  .display-4 {
    font-size: 2.5rem;
  }

  .region-cards {
    flex-direction: column;
  }

  .broker-benefits,
  .upload-options,
  .logos-grid,
  .relationship-data {
    grid-template-columns: 1fr;
  }

  .stp-workflow {
    flex-direction: column;
    gap: 1.5rem;
  }

  .workflow-arrow {
    transform: rotate(90deg);
  }

  .clob-header,
  .clob-row {
    grid-template-columns: 3fr 1fr 1fr;
  }

  .bid-qty, .ask-qty {
    display: none;
  }

  .integration-options {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 576px) {
  .benefits-grid {
    grid-template-columns: 1fr;
  }

  .code-features {
    grid-template-columns: 1fr;
  }

  .market-stat-row {
    flex-direction: column;
  }

  .market-stat {
    margin-bottom: 1rem;
  }

  .market-stat-row:last-child .market-stat:last-child {
    margin-bottom: 0;
  }

  .market-stats-card {
    padding: 1rem;
  }

  .display-4 {
    font-size: 2rem;
  }

  .lead {
    font-size: 1rem;
  }

  .report-types,
  .table-header,
  .table-row {
    grid-template-columns: 1fr;
  }

  .table-row span {
    margin-bottom: 0.5rem;
    text-align: left;
  }

  .table-row span:last-child {
    margin-bottom: 0;
  }

  /* Better button layout on mobile */
  .ct-btn-primary, .ct-btn-outline {
    display: block;
    width: 100%;
    margin-right: 0 !important;
    margin-bottom: 1rem;
    text-align: center;
  }

  .curve-controls {
    flex-direction: column;
  }

  .tab-item {
    padding: 0.5rem;
    font-size: 0.8rem;
  }

  .region-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>